// MotionBloomPass.ts
import {
	AdditiveBlending,
	Color,
	HalfFloatType,
	MeshBasicMaterial,
	ShaderMaterial,
	UniformsUtils,
	Vector2,
	Vector3,
	WebGLRenderer,
	WebGLRenderTarget,
	type RenderTargetOptions
} from 'three';
import { Pass, FullScreenQuad } from 'three/examples/jsm/postprocessing/Pass.js';
import { CopyShader } from 'three/examples/jsm/shaders/CopyShader.js';
import { LuminosityHighPassShader } from 'three/examples/jsm/shaders/LuminosityHighPassShader.js';

export interface MotionBloomParams {
	strength?: number;
	radius: number;
	threshold: number;
	resolution?: { x: number; y: number } | Vector2;
	dirX: Vector2;
	dirY: Vector2;
}

export class MotionBloomPass extends Pass {
	strength: number;
	radius: number;
	threshold: number;
	resolution: Vector2;
	dirX: Vector2;
	dirY: Vector2;

	clearColor = new Color(0, 0, 0);
	renderTargetsHorizontal: WebGLRenderTarget[] = [];
	renderTargetsVertical: WebGLRenderTarget[] = [];
	nMips = 5;

	renderTargetBright: WebGLRenderTarget;

	highPassUniforms: any;
	materialHighPassFilter: ShaderMaterial;

	separableBlurMaterials: ShaderMaterial[] = [];

	compositeMaterial: ShaderMaterial;
	bloomTintColors: Vector3[];

	copyUniforms: any;
	blendMaterial: ShaderMaterial;

	_oldClearColor = new Color();
	oldClearAlpha = 1;

	basic = new MeshBasicMaterial();
	fsQuad = new FullScreenQuad(undefined);

	constructor(params: MotionBloomParams) {
		super();

		this.strength = params.strength ?? 1;
		this.radius = params.radius;
		this.threshold = params.threshold;
		this.resolution =
			params.resolution instanceof Vector2
				? params.resolution.clone()
				: new Vector2((params.resolution as any)?.x ?? 256, (params.resolution as any)?.y ?? 256);

		this.dirX = params.dirX.clone();
		this.dirY = params.dirY.clone();

		// --- Render targets -----------------------------------------------------
		const rtOpts: RenderTargetOptions = { type: HalfFloatType };
		let resx = Math.round(this.resolution.x / 2);
		let resy = Math.round(this.resolution.y / 2);

		this.renderTargetBright = new WebGLRenderTarget(resx, resy, rtOpts);
		this.renderTargetBright.texture.name = 'MotionBloomPass.bright';
		this.renderTargetBright.texture.generateMipmaps = false;

		for (let i = 0; i < this.nMips; i++) {
			const h = new WebGLRenderTarget(resx, resy, rtOpts);
			h.texture.name = `MotionBloomPass.h${i}`;
			h.texture.generateMipmaps = false;
			this.renderTargetsHorizontal.push(h);

			const v = new WebGLRenderTarget(resx, resy, rtOpts);
			v.texture.name = `MotionBloomPass.v${i}`;
			v.texture.generateMipmaps = false;
			this.renderTargetsVertical.push(v);

			resx = Math.max(1, Math.round(resx / 2));
			resy = Math.max(1, Math.round(resy / 2));
		}

		// --- High-pass (bright areas) ------------------------------------------
		const highPassShader = LuminosityHighPassShader;
		this.highPassUniforms = UniformsUtils.clone(highPassShader.uniforms);
		this.highPassUniforms['luminosityThreshold'].value = this.threshold;
		this.highPassUniforms['smoothWidth'].value = 0.01;

		this.materialHighPassFilter = new ShaderMaterial({
			uniforms: this.highPassUniforms,
			vertexShader: highPassShader.vertexShader,
			fragmentShader: highPassShader.fragmentShader
		});

		// --- Gaussian/separable blur for each mip ------------------------------
		const kernelSizeArray = [3, 5, 7, 9, 11];
		resx = Math.round(this.resolution.x / 2);
		resy = Math.round(this.resolution.y / 2);

		for (let i = 0; i < this.nMips; i++) {
			const mat = this.getSeparableBlurMaterial(kernelSizeArray[i]);
			(mat.uniforms['invSize'].value as Vector2).set(1 / resx, 1 / resy);
			this.separableBlurMaterials.push(mat);

			resx = Math.max(1, Math.round(resx / 2));
			resy = Math.max(1, Math.round(resy / 2));
		}

		// --- Composite material -------------------------------------------------
		this.compositeMaterial = this.getCompositeMaterial(this.nMips);
		this.compositeMaterial.uniforms['blurTexture1'].value = this.renderTargetsVertical[0].texture;
		this.compositeMaterial.uniforms['blurTexture2'].value = this.renderTargetsVertical[1].texture;
		this.compositeMaterial.uniforms['blurTexture3'].value = this.renderTargetsVertical[2].texture;
		this.compositeMaterial.uniforms['blurTexture4'].value = this.renderTargetsVertical[3].texture;
		this.compositeMaterial.uniforms['blurTexture5'].value = this.renderTargetsVertical[4].texture;
		this.compositeMaterial.uniforms['bloomStrength'].value = this.strength;
		this.compositeMaterial.uniforms['bloomRadius'].value = 0.1;

		const bloomFactors = [1.0, 0.8, 0.6, 0.4, 0.2];
		this.compositeMaterial.uniforms['bloomFactors'].value = bloomFactors;

		this.bloomTintColors = [
			new Vector3(1, 1, 1),
			new Vector3(1, 1, 1),
			new Vector3(1, 1, 1),
			new Vector3(1, 1, 1),
			new Vector3(1, 1, 1)
		];
		this.compositeMaterial.uniforms['bloomTintColors'].value = this.bloomTintColors;

		// --- Additive blend material -------------------------------------------
		const copyShader = CopyShader;
		this.copyUniforms = UniformsUtils.clone(copyShader.uniforms);

		this.blendMaterial = new ShaderMaterial({
			uniforms: this.copyUniforms,
			vertexShader: copyShader.vertexShader,
			fragmentShader: copyShader.fragmentShader,
			blending: AdditiveBlending,
			depthTest: false,
			depthWrite: false,
			transparent: true
		});

		this.enabled = true;
		this.needsSwap = false;
	}

	dispose(): void {
		this.renderTargetsHorizontal.forEach((rt) => rt.dispose());
		this.renderTargetsVertical.forEach((rt) => rt.dispose());
		this.renderTargetBright.dispose();

		this.separableBlurMaterials.forEach((m) => m.dispose());
		this.compositeMaterial.dispose();
		this.blendMaterial.dispose();
		this.basic.dispose();
		this.fsQuad.dispose();
	}

	setSize(width: number, height: number): void {
		let resx = Math.round(width / 2);
		let resy = Math.round(height / 2);

		this.renderTargetBright.setSize(resx, resy);

		for (let i = 0; i < this.nMips; i++) {
			this.renderTargetsHorizontal[i].setSize(resx, resy);
			this.renderTargetsVertical[i].setSize(resx, resy);

			(this.separableBlurMaterials[i].uniforms['invSize'].value as Vector2).set(1 / resx, 1 / resy);

			resx = Math.max(1, Math.round(resx / 2));
			resy = Math.max(1, Math.round(resy / 2));
		}
	}

	render(
		renderer: WebGLRenderer,
		_writeBuffer: WebGLRenderTarget,
		readBuffer: WebGLRenderTarget,
		_deltaTime?: number,
		maskActive?: boolean
	): void {
		renderer.getClearColor(this._oldClearColor);
		this.oldClearAlpha = renderer.getClearAlpha();
		const oldAutoClear = renderer.autoClear;
		renderer.autoClear = false;

		renderer.setClearColor(this.clearColor, 0);
		if (maskActive) renderer.state.buffers.stencil.setTest(false);

		// Render input to screen if needed
		if (this.renderToScreen) {
			this.fsQuad.material = this.basic;
			this.basic.map = readBuffer.texture;
			renderer.setRenderTarget(null);
			renderer.clear();
			this.fsQuad.render(renderer);
		}

		// 1) High-pass: extract bright areas
		this.highPassUniforms['tDiffuse'].value = readBuffer.texture;
		this.highPassUniforms['luminosityThreshold'].value = this.threshold;
		this.fsQuad.material = this.materialHighPassFilter;
		renderer.setRenderTarget(this.renderTargetBright);
		renderer.clear();
		this.fsQuad.render(renderer);

		// 2) Progressive blur across mips
		let inputRT: WebGLRenderTarget = this.renderTargetBright;

		for (let i = 0; i < this.nMips; i++) {
			const blurMat = this.separableBlurMaterials[i];

			// Horizontal (dirX)
			blurMat.uniforms['colorTexture'].value = inputRT.texture;
			(blurMat.uniforms['direction'].value as Vector2).copy(this.dirX);
			renderer.setRenderTarget(this.renderTargetsHorizontal[i]);
			renderer.clear();
			this.fsQuad.material = blurMat;
			this.fsQuad.render(renderer);

			// Vertical (dirY)
			blurMat.uniforms['colorTexture'].value = this.renderTargetsHorizontal[i].texture;
			(blurMat.uniforms['direction'].value as Vector2).copy(this.dirY);
			renderer.setRenderTarget(this.renderTargetsVertical[i]);
			renderer.clear();
			this.fsQuad.material = blurMat;
			this.fsQuad.render(renderer);

			inputRT = this.renderTargetsVertical[i];
		}

		// 3) Composite
		this.fsQuad.material = this.compositeMaterial;
		this.compositeMaterial.uniforms['bloomStrength'].value = this.strength;
		this.compositeMaterial.uniforms['bloomRadius'].value = this.radius;
		this.compositeMaterial.uniforms['bloomTintColors'].value = this.bloomTintColors;

		renderer.setRenderTarget(this.renderTargetsHorizontal[0]);
		renderer.clear();
		this.fsQuad.render(renderer);

		// 4) Additively blend over input
		this.fsQuad.material = this.blendMaterial;
		this.copyUniforms['tDiffuse'].value = this.renderTargetsHorizontal[0].texture;

		if (maskActive) renderer.state.buffers.stencil.setTest(true);

		if (this.renderToScreen) {
			renderer.setRenderTarget(null);
			this.fsQuad.render(renderer);
		} else {
			renderer.setRenderTarget(readBuffer);
			this.fsQuad.render(renderer);
		}

		// restore
		renderer.setClearColor(this._oldClearColor, this.oldClearAlpha);
		renderer.autoClear = oldAutoClear;
	}

	getSeparableBlurMaterial(kernelRadius: number): ShaderMaterial {
		const coeffs: number[] = [];
		for (let i = 0; i < kernelRadius; i++) {
			const w = (0.39894 * Math.exp((-0.5 * i * i) / (kernelRadius * kernelRadius))) / kernelRadius;
			coeffs.push(w);
		}

		return new ShaderMaterial({
			defines: { KERNEL_RADIUS: kernelRadius },
			uniforms: {
				colorTexture: { value: null },
				invSize: { value: new Vector2(0.5, 0.5) },
				direction: { value: new Vector2(1, 0) },
				gaussianCoefficients: { value: coeffs }
			},
			vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
        }
      `,
			fragmentShader: `
        #include <common>
        varying vec2 vUv;
        uniform sampler2D colorTexture;
        uniform vec2 invSize;
        uniform vec2 direction;
        uniform float gaussianCoefficients[KERNEL_RADIUS];

        void main() {
          float weightSum = gaussianCoefficients[0];
          vec3 diffuseSum = texture2D( colorTexture, vUv ).rgb * weightSum;
          for ( int i = 1; i < KERNEL_RADIUS; i++ ) {
            float x = float(i);
            float w = gaussianCoefficients[i];
            vec2 uvOffset = direction * invSize * x;
            vec3 s1 = texture2D( colorTexture, vUv + uvOffset ).rgb;
            vec3 s2 = texture2D( colorTexture, vUv - uvOffset ).rgb;
            diffuseSum += (s1 + s2) * w;
            weightSum += 2.0 * w;
          }
          gl_FragColor = vec4(diffuseSum / weightSum, 1.0);
        }
      `
		});
	}

	getCompositeMaterial(nMips: number): ShaderMaterial {
		return new ShaderMaterial({
			defines: { NUM_MIPS: nMips },
			uniforms: {
				blurTexture1: { value: null },
				blurTexture2: { value: null },
				blurTexture3: { value: null },
				blurTexture4: { value: null },
				blurTexture5: { value: null },
				bloomStrength: { value: 1.0 },
				bloomFactors: { value: null },
				bloomTintColors: { value: null },
				bloomRadius: { value: 0.0 }
			},
			vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
        }
      `,
			fragmentShader: `
        varying vec2 vUv;
        uniform sampler2D blurTexture1;
        uniform sampler2D blurTexture2;
        uniform sampler2D blurTexture3;
        uniform sampler2D blurTexture4;
        uniform sampler2D blurTexture5;
        uniform float bloomStrength;
        uniform float bloomRadius;
        uniform float bloomFactors[NUM_MIPS];
        uniform vec3 bloomTintColors[NUM_MIPS];

        float lerpBloomFactor( const in float f ) {
          float mirrorFactor = 1.2 - f;
          return mix( f, mirrorFactor, bloomRadius );
        }

        void main() {
          gl_FragColor =
            bloomStrength * (
              lerpBloomFactor(bloomFactors[0]) * vec4(bloomTintColors[0], 1.0) * texture2D(blurTexture1, vUv) +
              lerpBloomFactor(bloomFactors[1]) * vec4(bloomTintColors[1], 1.0) * texture2D(blurTexture2, vUv) +
              lerpBloomFactor(bloomFactors[2]) * vec4(bloomTintColors[2], 1.0) * texture2D(blurTexture3, vUv) +
              lerpBloomFactor(bloomFactors[3]) * vec4(bloomTintColors[3], 1.0) * texture2D(blurTexture4, vUv) +
              lerpBloomFactor(bloomFactors[4]) * vec4(bloomTintColors[4], 1.0) * texture2D(blurTexture5, vUv)
            );
        }
      `
		});
	}
}
